import { AppStateService } from '@ajs/services/AppStateService';
import { fdxUI as FdxUI } from '@ajs/services/fdxUI';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppMenuTab } from '@app/core/models/enums/app-menu-tab.enum';
import { DbFieldModel } from '@app/databases/models/db-field.model';
import { DbModel } from '@app/databases/models/db.model';
import { DatabasesDataService } from '@app/databases/services/databases-data.service';
import { BulkSortOrderConflictData, BulkSortOrderResponse } from '@app/databases/services/responses/bulk-sort-order.response';
import { DbFieldsWithFeoMetaResponse } from '@app/databases/services/responses/db-fields-with-feo-meta.response';
import { SetPrimaryKeyResponse } from '@app/databases/services/responses/data.response';
import { ModalService } from '@app/modules/modals/services/modal.service';
import { IconDefinition, faCircleInfo, faGripDotsVertical, faKey, faSortAlt } from '@fortawesome/pro-solid-svg-icons';
import { EMPTY, Observable, Subject, finalize, from, merge, of, switchMap, tap, throwError, timer } from 'rxjs';
import { catchError, filter, map, mergeMap, take, takeUntil } from 'rxjs/operators';

enum PageStatus {
    Initial,
    Loading,
    Error,
    Done
}

export interface DatabaseFieldAugmented extends DbFieldModel {
    editing: boolean;
    isPrimaryKey: boolean;
    // The field_type as last loaded from the server, used to detect an admin
    // edit so it can be persisted via the PUT (the bulk reorder endpoint does
    // not handle field_type).
    originalFieldType: string;
}

@Component({
    selector: 'fdx-transformers-field-order-page',
    styleUrl: './transformers-field-order-page.component.scss',
    templateUrl: './transformers-field-order-page.component.html',
    standalone: false
})
export class TransformersFieldOrderPageComponent implements OnInit, OnDestroy {

    title: string = 'Field execution order';
    appMenuTab: AppMenuTab = AppMenuTab.DataSources;

    dbFieldTypes: string[] = ['TEXT', 'MEDIUMTEXT'];
    dbFields: DatabaseFieldAugmented[] = [];

    iconKey: IconDefinition = faKey;
    iconSort: IconDefinition = faSortAlt;
    iconGrip: IconDefinition = faGripDotsVertical;
    iconInfo: IconDefinition = faCircleInfo;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    pageStatus: PageStatus = PageStatus.Initial;
    errorText: string = '';

    updateStatus: string = '';

    /**
     * Last-seen FEO ordering version returned by the backend. Used for
     * optimistic locking on subsequent saves so concurrent edits surface as a
     * 409 instead of silently overwriting another user's change.
     */
    orderVersion: number | null = null;

    /**
     * True when a save was queued because the database was actively building.
     * Drives the "queued" banner/toast UX so users understand why the page
     * may show the prior order until the follow-up build completes.
     */
    pendingOrderQueued: boolean = false;

    /**
     * The order_version owned by the queued request. A build applies a queued
     * order without bumping the version, so when the queue clears with the
     * version unchanged the user's order was applied; if the version advanced,
     * another save superseded it. Used to show the correct completion message.
     */
    private pendingQueuedVersion: number | null = null;

    readonly primaryKeyTooltipText: string = `Select a unique identifier for Primary key by clicking
    on the key icon for a row.  Various features (Summary Governance, Data Governance, etc.) depend on the
    Primary key.`;

    readonly orderTooltipText: string = `Drag and drop a row or click on a number in the Order column
    to change the field execution order.  Transformers run according to the field execution order.`;

    get database(): DbModel {
        return this.appStateService.getDatabase();
    }

    get databaseId(): string {
        return this.appStateService.getDatabaseId();
    }

    get isAdmin(): boolean {
        return this.appStateService.isPrivacyLevelAtLeastAdmin();
    }

    get primaryKey(): string {
        return this.database.primary_db_field_key;
    }

    get requestedPrimaryKey(): string {
        return this.database.requested_primary_key;
    }

    private readonly unsubscribe$: Subject<void> = new Subject<void>();
    private readonly queuedOrderPollStop$: Subject<void> = new Subject<void>();

    private static readonly QUEUED_ORDER_POLL_MS: number = 5000;

    constructor(
        private readonly appStateService: AppStateService,
        private readonly databasesDataService: DatabasesDataService,
        private readonly modalService: ModalService,
        private readonly fdxUI: FdxUI
    ) { }

    ngOnInit(): void {
        this.fdxUI.setTitle(this.title);
        this.fdxUI.setActiveTab(this.appMenuTab);

        this.pageStatus = PageStatus.Loading;

        this.watchDatabaseRefresh();

        this.fetchDbFields()
            .pipe(
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: () => {
                    this.pageStatus = PageStatus.Done;
                }
            });
    }

    ngOnDestroy(): void {
        this.queuedOrderPollStop$.next();
        this.queuedOrderPollStop$.complete();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    trackDbField(_index: number, field: DatabaseFieldAugmented): string {
        return field.id;
    }

    drop(event: CdkDragDrop<DatabaseFieldAugmented[], DatabaseFieldAugmented[], DatabaseFieldAugmented>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        moveItemInArray(this.dbFields, event.previousIndex, event.currentIndex);

        const modalRef = this.modalService.showConfirmationModal(
            'Change sort order',
            `You're about to move ${event.item.data.field_name} from position ${event.previousIndex} to position ${event.currentIndex}.`,
            'Change order'
        );

        modalRef.closed
            .pipe(
                takeUntil(this.unsubscribe$)
            )
            .subscribe(() => {
                this.reorderClicked(this.dbFields[event.currentIndex], `${event.currentIndex}`);
            });

        modalRef.dismissed
            .pipe(
                takeUntil(this.unsubscribe$)
            )
            .subscribe(() => {
                moveItemInArray(this.dbFields, event.currentIndex, event.previousIndex);
            });
    }

    fetchDbFields(options: { blockUi?: boolean; detectQueued?: boolean; } = {}): Observable<DatabaseFieldAugmented[]> {
        const blockUi = options.blockUi !== false;
        const detectQueued = options.detectQueued !== false;

        if (blockUi) {
            this.fdxUI.startBlockUI();
        }

        return this.databasesDataService.getFieldsWithFeoMeta(this.databaseId)
            .pipe(
                tap((response) => {
                    if (typeof response.meta?.order_version === 'number') {
                        this.orderVersion = response.meta.order_version;
                    }
                    // Surface a still-pending queued request (e.g. the page was
                    // reloaded while a build is applying a queued reorder) so the
                    // banner shows and polling resumes instead of silently
                    // dropping it. Guard prevents restarting an active poll.
                    //
                    // Skipped (detectQueued=false) right after an immediate save
                    // applied the order: a stale queued row left by a failed
                    // best-effort clear must not re-raise the banner for an order
                    // that was already written.
                    if (detectQueued && response.meta?.queued === true && !this.pendingOrderQueued) {
                        this.pendingOrderQueued = true;
                        this.pendingQueuedVersion = response.meta.order_version;
                        this.startQueuedOrderPolling();
                    }
                }),
                map((response) =>
                    response.data.map((field: DbFieldModel): DatabaseFieldAugmented => {
                        return {
                            ...field,
                            editing: false,
                            isPrimaryKey: this.isPrimaryKey(field),
                            originalFieldType: field.field_type
                        };
                    })
                ),
                tap((fields: DatabaseFieldAugmented[]) => {
                    this.dbFields = fields;
                }),
                finalize(() => {
                    if (blockUi) {
                        this.fdxUI.stopBlockUI();
                    }
                })
            );
    }

    isPrimaryKey(dbField: DbFieldModel): boolean {
        return dbField.field_name === this.primaryKey;
    }

    /**
     * `AppComponent` reloads the database on every navigation, so the
     * `primary_db_field_key` backing this page can arrive *after* the db_fields
     * response has already been mapped — and a build (e.g. AFB) can change it
     * server-side while the cached DbModel is still in memory. Re-derive the
     * per-row flag whenever that refresh lands; without it the key icon and the
     * `openPrimaryKeyModal` guard keep the value snapshotted at map time and
     * never correct themselves.
     */
    private watchDatabaseRefresh(): void {
        this.appStateService.databaseLoaded$
            .pipe(
                filter((database: DbModel | null): database is DbModel => database !== null),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(() => {
                // Spread rather than reassign so an in-progress row edit
                // (editing, typed sort_order, field_type) survives the refresh.
                this.dbFields = this.dbFields.map((field: DatabaseFieldAugmented): DatabaseFieldAugmented => ({
                    ...field,
                    isPrimaryKey: this.isPrimaryKey(field)
                }));
            });
    }

    openPrimaryKeyModal(dbField: DatabaseFieldAugmented): void {
        if (dbField.isPrimaryKey) {
            return;
        }

        const titleText = this.primaryKey ? `Change Primary Key to ${dbField.field_name}?` : `Select ${dbField.field_name} as Primary Key?`;

        const warningText = this.primaryKey
            ? `Are you sure you want to change the primary key from <strong>${this.primaryKey}</strong> to <strong>${dbField.field_name}</strong>?`
            : `Are you sure you want to select <strong>${dbField.field_name}</strong> as the primary key?`;


        let bodyText = 'Primary Key is typically a unique identifier such as SKU, ID, etc. If it is not, this may lead to unexpected behavior across the platform.';
        bodyText += `<br /><br /><span class="text-warning">${warningText}</span><br /><br />`;

        const confirmText = this.primaryKey
            ? 'Confirm change'
            : 'Confirm selection';

        const modalRef = this.modalService.showWarningConfirmationModal(
            titleText,
            bodyText,
            confirmText
        );

        modalRef.closed
            .pipe(
                takeUntil(this.unsubscribe$)
            )
            .subscribe(() => {
                this.fdxUI.startBlockUI();

                this.errorText = '';

                this.databasesDataService.setPrimaryKey(this.databaseId, dbField.field_name)
                    .pipe(
                        switchMap((res: SetPrimaryKeyResponse) => {
                            if (res.update_status !== undefined) {
                                this.updateStatus = res.update_status;
                            }
                            if (res?.status === 'fail') {
                                return throwError(res);
                            }

                            return from(
                                this.appStateService.loadDatabase(this.databaseId)
                            );
                        }),
                        takeUntil(this.unsubscribe$)
                    )
                    .subscribe(
                        {
                            next: () => {
                                this.fetchDbFields()
                                    .pipe(
                                        takeUntil(this.unsubscribe$)
                                    )
                                    .subscribe(
                                        () => {
                                            if (this.updateStatus === 'updated') {
                                                this.fdxUI.showToastSuccess(this.primaryKey ? `Primary key has been changed to ${dbField.field_name}!` : `Primary key has been set to: ${dbField.field_name}!`);
                                            } else if (this.updateStatus === 'requested') {
                                                this.fdxUI.showToastInfo('The system is processing your selection of primary key. You will receive an email upon completion.');
                                            } else if (this.updateStatus === 'duplicate') {
                                                this.fdxUI.showToastError('The selected field is not unique, please select a field that has unique values.');
                                            } else {
                                                this.fdxUI.showToastError('Primary key update failed');
                                            }
                                        }
                                    );
                            },
                            complete: () => {
                                this.fdxUI.stopBlockUI();
                            },
                            error: (_error: HttpErrorResponse) => {
                                this.fdxUI.stopBlockUI();
                                this.fdxUI.showToastError('Primary key update failed');
                            }
                        }
                    );
            });
    }

    reorderClicked(dbField: DatabaseFieldAugmented, sortOrder: `${number}`): void {
        // The Order column "Update" action edits dbField.sort_order in place
        // without changing the dbFields array order, so move the field to the
        // requested position before serializing. Drag-and-drop already moved
        // the array and passes the field's current index, making this a no-op.
        //
        // The page always renders contiguous order values (0..N-1) — the bulk
        // endpoint, build worker, and legacy PUT all keep sort_order a
        // contiguous permutation — so the entered value maps 1:1 to the target
        // position. Out-of-range input is rejected (not silently clamped to a
        // different row).
        const targetIndex = this.resolveSortOrderTarget(sortOrder);
        if (targetIndex === null) {
            this.errorText = 'Enter a valid position number.';
            this.fetchDbFields()
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe();
            return;
        }

        // Apply the move optimistically (no-op for drag-drop, which already
        // moved the array and passes the field's current index).
        const currentIndex = this.dbFields.findIndex((field) => field.id === dbField.id);
        const orderChanged = currentIndex !== -1 && currentIndex !== targetIndex;
        if (orderChanged) {
            moveItemInArray(this.dbFields, currentIndex, targetIndex);
        }

        // The bulk reorder endpoint only handles sort_order. An admin field_type
        // edit must go through the PUT (which persists type + order). Route to
        // it when the type changed; otherwise use the build-safe bulk endpoint.
        if (dbField.field_type !== dbField.originalFieldType) {
            this.updateFieldType(dbField, sortOrder);
            return;
        }

        this.submitOrder();
    }

    /**
     * Submits the current dbFields order via the build-safe bulk endpoint.
     */
    private submitOrder(): void {
        this.fdxUI.startBlockUI();
        this.errorText = '';

        const payload: { db_fields: { field_name: string; sort_order: number; }[]; order_version?: number; } = {
            db_fields: this.dbFields.map((field, index) => ({
                field_name: field.field_name,
                sort_order: index
            }))
        };
        if (this.orderVersion !== null) {
            payload.order_version = this.orderVersion;
        }

        this.databasesDataService.bulkUpdateFieldSortOrders(this.databaseId, payload)
            .pipe(
                finalize(() => {
                    this.fdxUI.stopBlockUI();
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: (response: BulkSortOrderResponse) => {
                    this.handleBulkSortOrderResponse(response);
                },
                error: (error: HttpErrorResponse) => {
                    this.handleBulkSortOrderError(error);
                }
            });
    }

    /**
     * Persists a field_type change (and that row's order) via the PUT endpoint,
     * which handles both. During a build the backend may return HTTP 202 with
     * the order queued and field_type_rejected set when the type cannot apply yet.
     */
    private updateFieldType(dbField: DatabaseFieldAugmented, sortOrder: `${number}`): void {
        this.fdxUI.startBlockUI();
        this.errorText = '';

        this.databasesDataService.updateField(this.databaseId, {
            field_name: dbField.field_name,
            field_type: dbField.field_type,
            sort_order: sortOrder
        })
            .pipe(
                mergeMap((response: unknown) => {
                    const putQueued = this.parsePutFieldQueuedResponse(response);
                    if (putQueued !== null) {
                        if (putQueued.field_type_rejected) {
                            dbField.field_type = dbField.originalFieldType;
                            this.fdxUI.showToastError(
                                putQueued.field_type_rejection_message
                                ?? 'Cannot change field type while a database build is running. Please try again after the build completes.'
                            );
                        }
                        this.applyPutQueuedOrderState(putQueued);
                        return of(null);
                    }
                    // Applied immediately; don't let a stale queued row re-raise the
                    // banner (detectQueued=false).
                    return this.fetchDbFields({ detectQueued: false });
                }),
                finalize(() => {
                    this.fdxUI.stopBlockUI();
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                error: (error: HttpErrorResponse) => {
                    this.errorText = error.error?.message ?? 'Failed to update field type';
                }
            });
    }

    /**
     * PUT /db_fields returns HTTP 202 with { data: { queued: true, ... } } while building.
     */
    private parsePutFieldQueuedResponse(response: unknown): {
        order_version?: number;
        field_type_rejected?: boolean;
        field_type_rejection_message?: string;
    } | null {
        if (!response || typeof response !== 'object') {
            return null;
        }
        const body = response as {
            data?: {
                queued?: boolean;
                order_version?: number;
                field_type_rejected?: boolean;
                field_type_rejection_message?: string;
            };
        };
        if (body.data?.queued !== true) {
            return null;
        }
        return {
            order_version: body.data.order_version,
            field_type_rejected: body.data.field_type_rejected === true,
            field_type_rejection_message: body.data.field_type_rejection_message,
        };
    }

    /** Applies queued-order UI state from a build-safe PUT 202 (no bulk follow-up). */
    private applyPutQueuedOrderState(queued: {
        order_version?: number;
        field_type_rejected?: boolean;
        field_type_rejection_message?: string;
    }): void {
        if (typeof queued.order_version === 'number') {
            this.orderVersion = queued.order_version;
            this.pendingQueuedVersion = queued.order_version;
        }
        this.pendingOrderQueued = true;
        this.renumberSortOrders();
        this.fdxUI.showToastInfo(
            'Database is currently building. The new field execution order has been queued and will be applied after the build completes.'
        );
        this.startQueuedOrderPolling();
    }

    /**
     * Parses a requested Order-column value into a valid target position, or
     * null when the input is not a whole number or is outside the valid range
     * [0, N-1]. Out-of-range values are rejected rather than clamped so a typo
     * never silently moves the field to a different row.
     */
    private resolveSortOrderTarget(sortOrder: `${number}`): number | null {
        const parsed = Number(sortOrder);
        if (!Number.isInteger(parsed)) {
            return null;
        }
        if (parsed < 0 || parsed > this.dbFields.length - 1) {
            return null;
        }
        return parsed;
    }

    /**
     * Reassigns each row's displayed sort_order to its current array position
     * so the Order badges show a contiguous 0..N-1 sequence matching the
     * on-screen order. Used after a queued reorder, where re-fetching would
     * revert the optimistic move (db_fields is not updated until the build
     * applies the queue).
     */
    private renumberSortOrders(): void {
        this.dbFields = this.dbFields.map((field, index) => ({
            ...field,
            sort_order: `${index}`,
            editing: false
        }));
    }

    private handleBulkSortOrderResponse(response: BulkSortOrderResponse): void {
        const meta = response.meta;
        if (meta?.queued === true) {
            this.orderVersion = meta.order_version;
            this.pendingOrderQueued = true;
            this.pendingQueuedVersion = meta.order_version;
            // Renumber the Order badges to match the queued positions so the
            // list visibly reflects the pending order. The optimistic move
            // already reordered the rows; db_fields is not updated until the
            // build applies the queue, so we cannot re-fetch here without
            // reverting that visual move. The "queued" banner explains it is
            // pending until the build completes.
            this.renumberSortOrders();
            this.fdxUI.showToastInfo(
                'Database is currently building. The new field execution order has been queued and will be applied after the build completes.'
            );
            this.startQueuedOrderPolling();
            return;
        }

        if (meta && typeof meta.order_version === 'number') {
            this.orderVersion = meta.order_version;
        }
        this.pendingOrderQueued = false;
        this.pendingQueuedVersion = null;
        this.queuedOrderPollStop$.next();

        // Re-pull fields to ensure UI reflects authoritative server state. The
        // order was just applied immediately, so don't let a stale queued row
        // re-raise the banner (detectQueued=false).
        this.fetchDbFields({ detectQueued: false })
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe();
    }

    /**
     * Poll the FEO metadata while a queued reorder is pending. The backend's
     * `queued` flag is the authoritative signal: it clears once a build applies
     * the queued order (or an immediate save supersedes it). Using it avoids
     * guessing from field order, which breaks when a build adds/removes columns.
     */
    private startQueuedOrderPolling(): void {
        this.queuedOrderPollStop$.next();

        timer(
            TransformersFieldOrderPageComponent.QUEUED_ORDER_POLL_MS,
            TransformersFieldOrderPageComponent.QUEUED_ORDER_POLL_MS
        )
            .pipe(
                // A transient poll failure must not kill the stream (which would
                // stop polling and leave the banner stuck). Swallow per-tick
                // errors so the next timer tick retries.
                switchMap(() => this.databasesDataService.getFieldsWithFeoMeta(this.databaseId).pipe(
                    catchError(() => EMPTY)
                )),
                filter((response) => response.meta?.queued === false),
                take(1),
                switchMap((response) =>
                    // The queue already cleared on the server; if the refresh
                    // fails, still proceed so the banner is cleared.
                    this.fetchDbFields({ blockUi: false }).pipe(
                        map(() => response),
                        catchError(() => of(response))
                    )
                ),
                takeUntil(merge(this.queuedOrderPollStop$, this.unsubscribe$))
            )
            .subscribe({
                next: (response: DbFieldsWithFeoMetaResponse) => {
                    if (!this.pendingOrderQueued) {
                        return;
                    }

                    // The queue cleared. If the version is still the one we
                    // queued, the build applied our order. If it advanced,
                    // another save superseded it, so avoid a misleading
                    // "applied" message.
                    const appliedOurOrder = this.pendingQueuedVersion !== null
                        && response.meta?.order_version === this.pendingQueuedVersion;

                    this.pendingOrderQueued = false;
                    this.pendingQueuedVersion = null;

                    if (appliedOurOrder) {
                        this.fdxUI.showToastSuccess('Your field execution order has been applied.');
                    } else {
                        // The queue cleared without our version surviving: another
                        // save superseded it, or the queued request expired. We
                        // cannot tell which, so use a neutral message and show the
                        // refreshed current order.
                        this.fdxUI.showToastInfo('Your queued field execution order was not applied. Please review the current order.');
                    }
                }
            });
    }

    private handleBulkSortOrderError(error: HttpErrorResponse): void {
        const errorBody: { data?: BulkSortOrderConflictData; message?: string; } = error.error ?? {};

        if (error.status === 409) {
            const conflictData = errorBody.data;
            if (conflictData && typeof conflictData.order_version === 'number') {
                this.orderVersion = conflictData.order_version;
            }
            this.fdxUI.showToastError(
                'Field execution order has changed since you loaded the page. Refreshing...'
            );
            this.fetchDbFields()
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe();
            return;
        }

        this.errorText = errorBody.message ?? 'Failed to update field execution order';
        this.fetchDbFields()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe();
    }
}
